import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingSubscriptionService } from './generated/billing-subscription/billing-subscription.service';
import type {
  BillingV1SubscriptionsListParams,
  PatchedSubscriptionUpdateRequestV1Request,
  SubscriptionCreateRequestV1Request,
  SubscriptionV1,
} from './generated/types';

/**
 * TARG-4 pure: injects `BillingSubscriptionService` directly, no facade.
 * Same shape as `BillingItemsStore` (iter 1). The DELETE method is the first
 * of the paraguas — `removeEntity` in the tap keeps the cursor page valid
 * (server list order is untouched, one row disappears).
 *
 * COMPONENT-DRIVEN: `dt-subscriptions` calls `load({...})` on init with the
 * current filter (`user_id`); the store does NOT auto-load.
 */
export const SubscriptionsStore = signalStore(
  withEntities<SubscriptionV1>(),
  withCursorPagination<SubscriptionV1, BillingV1SubscriptionsListParams>(() => {
    const api = inject(BillingSubscriptionService);
    // The generated page already is { results, next, previous }: no adapter.
    return (query, cursor) =>
      api.billingV1SubscriptionsList({ ...query, cursor: cursor ?? undefined });
  }),
  withMethods((store, api = inject(BillingSubscriptionService)) => ({
    createSubscription(body: SubscriptionCreateRequestV1Request) {
      return api.billingV1SubscriptionsCreate(body).pipe(
        tap(() => {
          store.load({});
        }),
        map((res) => res.subscription),
      );
    },
    updateSubscription(
      body: PatchedSubscriptionUpdateRequestV1Request,
      identity: string,
    ) {
      return api.billingV1SubscriptionsPartialUpdate({ identity }, body).pipe(
        tap((res) => {
          patchState(
            store,
            updateEntity({
              id: res.subscription.id,
              changes: res.subscription,
            }),
          );
        }),
        map((res) => res.subscription),
      );
    },
    deleteSubscription(identity: string, id: number) {
      return api.billingV1SubscriptionsDestroy({ identity }).pipe(
        tap(() => {
          patchState(store, removeEntity(id));
        }),
      );
    },
  })),
);
